import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Center,
  Loader,
  Paper,
  Code,
  Text,
  Badge,
  NumberFormatter,
  Group,
  Button,
  Checkbox,
  Table,
  Tooltip,
} from "@mantine/core";
import { getAdminJobDetails, requeueJob, requeueJobHard } from "../api";
import JobStatusBadge from "./JobStatusBadge";
import { Link } from "wouter";
import { notifications } from "@mantine/notifications";
import React from "react";

function LogLevelBadge({ size = "md", level, ...props }) {
  const levelLower = level.toLowerCase();
  switch (levelLower) {
    case "error":
    case "critical":
      return (
        <Badge size={size} variant="light" color="red" {...props}>
          {levelLower === "critical" ? "Critical" : "Error"}
        </Badge>
      );
    case "warn":
    case "warning":
      return (
        <Badge size={size} variant="light" color="yellow" {...props}>
          Warning
        </Badge>
      );
    case "info":
    case "information":
      return (
        <Badge size={size} variant="light" color="blue" {...props}>
          Info
        </Badge>
      );
    default:
      return (
        <Badge size={size} variant="light" color="gray" {...props}>
          {level}
        </Badge>
      );
  }
}

// Compact time-of-day with milliseconds for the log stream. The date is the same
// for the whole job (shown in the header), so the per-row time-only form is enough
// and leaves room for the severity badge + origin without truncation. Kept in UTC
// (with an explicit "Z") so it matches the raw UTC timestamps shown elsewhere on the
// page (firstStarted, execution rows) rather than silently drifting to local time.
const formatLogTime = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const pad = (n, len = 2) => String(n).padStart(len, "0");
  return (
    pad(d.getUTCHours()) +
    ":" +
    pad(d.getUTCMinutes()) +
    ":" +
    pad(d.getUTCSeconds()) +
    "." +
    pad(d.getUTCMilliseconds(), 3) +
    "Z"
  );
};

function JobLogDetails({ logs }) {
  return (
    <Table pl="md" mb="xl">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Timestamp</Table.Th>
          <Table.Th>Machine</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {logs?.map((log) => (
          <>
            <Table.Tr>
              <Table.Td>{log.idCreated}</Table.Td>
              <Table.Td>{log.machineName}</Table.Td>
              <Table.Td>
                <NumberFormatter
                  thousandSeparator
                  value={log.milliseconds}
                  suffix="ms"
                />
              </Table.Td>
              <Table.Td>
                <JobStatusBadge variant="light" status={log.status} />
              </Table.Td>
            </Table.Tr>
            {log.state && (
              <Table.Tr mt="md" mb="md">
                <Table.Td colSpan={4}>
                  <Text mb="xs">State</Text>
                  <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(log.state, null, 2)}</Code>
                </Table.Td>
              </Table.Tr>
            )}
          </>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export default function JobDetails({ params }) {
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [jobType, setJobType] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [autoReload, setAutoReload] = useState(true); // default enabled

  const RELOAD_INTERVAL_MS = 15000; // 15s

  const loadData = useCallback(() => {
    setIsLoading(true);
    getAdminJobDetails(params.id)
      .then((data) => {
        setJob(data.job);
        setLogs(data.logs);
        setJobType(data.type);
        setLogEntries(data.logEntries.toReversed() || []);
      })
      .finally(() => setIsLoading(false));
  }, [params.id]);

  useEffect(() => {
    loadData();
  }, [params.id, loadData]);

  useEffect(() => {
    if (!autoReload) return;
    const interval = setInterval(() => {
      loadData();
    }, RELOAD_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoReload, loadData, RELOAD_INTERVAL_MS]);

  if (!job)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  return (
    <Container fluid px={0}>
      <Paper p="md" radius="md" withBorder>
        {/* <Code block>{JSON.stringify(job, null, 2)}</Code> */}
        <Group justify="space-between" align="flex-start">
          <Group>
            <Text fw={500} fz="lg">
              {job.id} ({jobType})
            </Text>
            <Text>{job.originalFileName}</Text>
            {isLoading && <Loader size="sm" />}
          </Group>
          <Group gap="md">
            <Checkbox
              size="sm"
              label="Auto-refresh (15s)"
              checked={autoReload}
              onChange={(e) => setAutoReload(e.currentTarget.checked)}
            />
            <Button
              size="xs"
              variant="light"
              onClick={loadData}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Group>
        </Group>
        <Group>
          <JobStatusBadge status={job.status} />
          <Text c="dimmed">First started at: {job.firstStarted}</Text>
        </Group>
        <Group justify="flex-end">
          <Button
            size="sm"
            my="md"
            color="blue"
            onClick={() => {
              setIsLoading(true);
              requeueJob(job.id)
                .then(() => {
                  notifications.show({
                    title: "Job re-queued",
                    color: "green",
                  });
                  loadData();
                })
                .catch((error) => {
                  notifications.show({
                    title: "Failed to re-queue job",
                    message: "" + error,
                    color: "red",
                  });
                  setIsLoading(false);
                });
            }}
          >
            Re-Queue Job
          </Button>

          <Tooltip label="Re-queues the job, setting its retry count and original start to null. It also resets progress, cancellation requests etc. It does NOT reset the job's internal state, however.">
            <Button
              size="sm"
              my="md"
              color="red"
              onClick={() => {
                setIsLoading(true);
                requeueJobHard(job.id)
                  .then(() => {
                    notifications.show({
                      title: "Job re-queued HARD",
                      color: "green",
                    });
                    loadData();
                  })
                  .catch((error) => {
                    notifications.show({
                      title: "Failed to re-queue job",
                      message: "" + error,
                      color: "red",
                    });
                    setIsLoading(false);
                  });
              }}
            >
              Re-Queue Job HARD
            </Button>
          </Tooltip>
        </Group>

        {job.documentFileId && (
          <Paper my="md">
            Processed file:{" "}
            <Link to={`/documents/${job.documentFileId}`}>
              {job.documentFileId}
            </Link>
          </Paper>
        )}

        {job.tenderId && (
          <Paper my="md">
            Processed tender:{" "}
            <Link to={`/tenders/${job.tenderId}`}>{job.tenderId}</Link>
          </Paper>
        )}

        <Table style={{ tableLayout: "fixed" }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "260px" }}>Timestamp</Table.Th>
              <Table.Th>Message</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logEntries?.map((entry) => (
              <React.Fragment key={entry.id}>
                <Table.Tr>
                  <Table.Td style={{ width: "260px", verticalAlign: "top" }}>
                    <Group gap="xs" wrap="nowrap" align="center">
                      <LogLevelBadge
                        size="xs"
                        level={entry.level}
                        style={{ flexShrink: 0 }}
                      />
                      <Text
                        ff="monospace"
                        fz="xs"
                        c="dimmed"
                        title={entry.timestamp}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {formatLogTime(entry.timestamp)}
                      </Text>
                    </Group>
                    <Tooltip
                      label={entry.name}
                      multiline
                      maw={460}
                      withArrow
                      openDelay={300}
                    >
                      <Text truncate="start" fz={11} c="dimmed">
                        {entry.name}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td style={{ wordBreak: "break-word" }}>
                    <Text fz="xs" style={{ whiteSpace: "pre-wrap" }}>{entry.message}</Text>
                  </Table.Td>
                </Table.Tr>
                {entry.exception && (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      {entry.exception && (
                        <Code
                          block
                          style={{
                            fontSize: "11px",
                            textWrap: "auto",
                          }}
                        >
                          {entry.exception}
                        </Code>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )}
              </React.Fragment>
            ))}
          </Table.Tbody>
        </Table>

        <Text mt="lg" fw={500} fz="lg">
          Executions
        </Text>
        <JobLogDetails logs={logs} />
      </Paper>

      <Text mt={100}>Full job JSON</Text>
      <Code mt="sm" block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(job, null, 2)}
      </Code>
    </Container>
  );
}
